/**
 * Pure shaping for a task list (DD-052): the item's own tasks first, then
 * one via group per related item, and the done count across them.
 */

import type { Task, TaskList, TaskViaItem } from "./types";

export interface TaskListSection {
  key: string;
  /** Null for the item's own tasks, else the item they came through. */
  item: TaskViaItem | null;
  tasks: Task[];
}

/** Own tasks (when there are any), then each non-empty via group in the
 * server's order. */
export function taskListSections(list: TaskList): TaskListSection[] {
  const own: TaskListSection[] = list.tasks.length > 0 ? [{ key: "own", item: null, tasks: list.tasks }] : [];
  const via = list.via
    .filter((group) => group.tasks.length > 0)
    .map((group) => ({ key: `${group.item.type}:${group.item.id}`, item: group.item, tasks: group.tasks }));
  return [...own, ...via];
}

/** Done and total over every task the list shows. A task in two via groups
 * is one task, counted once. */
export function taskListProgress(list: TaskList): { done: number; total: number } {
  const byId = new Map<string, Task>();
  for (const task of list.tasks) byId.set(task.id, task);
  for (const group of list.via) {
    for (const task of group.tasks) byId.set(task.id, task);
  }
  const tasks = Array.from(byId.values());
  return { done: tasks.filter((task) => task.status === "done").length, total: tasks.length };
}
